import {useFormik} from "formik";
import {useAtom} from "jotai";
import {useEffect, useState} from "react";
import {toast} from "sonner";
import * as yup from 'yup';
import {formsSubmitAtom} from "../../../atom/atom";
import CustomSelect from "../../../components/forms/CustomSelect";
import RequiredInput from "../../../components/forms/InputHOC";
import {usePrivilege} from "../../../components/priviledge/PriviledgeProvider";
import useHandleErrors from "../../../hooks/useHandleErrors";
import {InvoiceService} from "../../../services/invoices/invoiceService";
import moment from "moment";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import useClients from "../../../hooks/api-calls/useClients";
import { Input } from "rizzui";

interface InvoiceFormProps {
    invoiceSelected: any;
    formRef?: any;
    setOpenModal: any;
    refetch: () => void;
    clientId?: string;
    isPaid?: boolean;
}

const editSchema = yup.object({
    invoiceDate: yup.date().required('Campo obligatorio'),
    kgQuantity: yup.number().min(1, 'Cantidad debe ser mayor a 0').required('Campo obligatorio'),
    unitPrice: yup.number().min(1, 'Cantidad debe ser mayor a 0').required('Campo obligatorio'),
    ivaTax: yup.number().min(0, 'Cantidad no puede ser negativa').max(100, 'Cantidad no puede ser superior a 100').required('Campo obligatorio').default(21),
    comments: yup.string().min(0).max(500, 'Demasiado largo').nullable(),
    cancelReason: yup.string().min(0).max(500, 'Demasiado largo').nullable(),
    clientId: yup.string().required('Campo obligatorio'),
});


const schema = yup.object({
    invoiceDate: yup.date().required('Campo obligatorio'),
    kgQuantity: yup.number().min(1, 'Cantidad debe ser mayor a 0').required('Campo obligatorio'),
    unitPrice: yup.number().min(1, 'Cantidad debe ser mayor a 0').required('Campo obligatorio'),
    ivaTax: yup.number().min(0, 'Cantidad no puede ser negativa').max(100, 'Cantidad no puede ser superior a 100').required('Campo obligatorio').default(21),
    comments: yup.string().min(0).max(500, 'Demasiado largo').nullable(),
    cancelReason: yup.string().min(0).max(500, 'Demasiado largo').nullable(),
    isCancel: yup.boolean().default(false),
    isPaid: yup.boolean().default(false),
    clientId: yup.string().required('Campo obligatorio'),
});

const lockedSchema = yup.object({
    comments: yup.string().min(0).max(500, 'Demasiado largo').nullable(),
});

const activeSchema = schema;
const activeEditSchema = editSchema;

const InvoiceForm: React.FC<InvoiceFormProps> = ({ invoiceSelected, formRef, setOpenModal, refetch, clientId = null, isPaid = false }) => {

    // STATES

    const [isSearched, setIsSearched] = useState<boolean>(false);
    const [isSearchedForClient, setIsSearchedForClient] = useState<boolean>(false);
    const [, setIsSubmitting] = useAtom(formsSubmitAtom);

    // HOOKS

    const service = new InvoiceService();
    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const { getCompaniesList } = useCompanies();
    const { getClientsList, updateHookFilters } = useClients();

    // FLAG DE BLOQUEO
    const isLocked = !!(invoiceSelected?.isPaid || invoiceSelected?.isCancel); 
    const isDocumentPaid = !!(invoiceSelected?.isPaid || isPaid);
    const docName = isDocumentPaid ? "Factura" : "Proforma";

    // FUNCTIONS

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJAR EL ENVIO DEL FORMULARIO DE EDICION
     * @EN HANDLE THE SUBMIT OF THE EDIT FORM
     */
    //--------------------------------------------------------------------------------------------------------------
    const handleUpdate = async (values: any) => {
        try {
            const response = (await service.editInvoice(values)).getResponseData();

            if (response.success) {
                toast.success(`${docName} editada correctamente`);
                refetch();
                setOpenModal(false);
            } else {
                handleErrors(response);
                return;
            }
        } catch (error: any) {
            toast.error(`Error al editar la ${docName.toLowerCase()}`);
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJAR EL ENVIO DEL FORMULARIO DE CREACION
     * @EN HANDLE THE SUBMIT OF THE CREATE FORM
     *
     * @param values
     */
    //--------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            const payload = {
                ...values,
                isPaid: isPaid
            };
            const response = (await service.createInvoice(payload)).getResponseData();

            if (response.success) {
                setOpenModal(false);
                refetch();
                toast.success(`${docName} ${isPaid ? 'creada' : 'emitida'} correctamente`);
            } else {
                handleErrors(response);
                return;
            }
        } catch (error: any) {
            toast.error(`Error al ${isPaid ? 'crear' : 'emitir'} la ${docName.toLowerCase()}`);
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    // FORMIK

    const formik = useFormik({
        initialValues: {
            id: invoiceSelected?.id ?? '',
            company: invoiceSelected?.company?.id ?? '',
            invoiceDate: invoiceSelected?.invoiceDate ? moment(invoiceSelected?.invoiceDate?.date).format('YYYY-MM-DD') : '',
            kgQuantity: invoiceSelected?.kgQuantity ?? '',
            unitPrice: invoiceSelected?.unitPrice ?? '',
            totalAmount: invoiceSelected?.totalAmount ?? '',
            ivaTax: invoiceSelected?.ivaTax ?? 21,
            comments: invoiceSelected?.comments ?? '',
            cancelReason: invoiceSelected?.cancelReason ?? '',
            clientId: clientId ? clientId : invoiceSelected?.client?.id ?? ''
        },
        validationSchema: isLocked
            ? lockedSchema
            : (invoiceSelected ? activeEditSchema : activeSchema),
        validateOnBlur: false,
        onSubmit: values => {
            if (invoiceSelected) {
                // 4) CUANDO ESTÁ BLOQUEADA, SOLO ACTUALIZAMOS COMENTARIO
                if (isLocked) {
                    const payload = { id: values.id, comments: values.comments ?? '' };
                    handleUpdate(payload);
                } else {
                    handleUpdate(values);
                }
            } else {
                handleCreate(values);
            }
        },
    });

    // USE EFFECTS

    useEffect(() => {
        setIsSubmitting(formik.isSubmitting);
    }, [formik.isSubmitting, setIsSubmitting]);

    useEffect(() => {
        if (!formik.values.company && getCompaniesList().length > 0 && !isSearched) {
            setIsSearched(true);
            let defaultCompany = getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU');
            formik.setFieldValue('company', defaultCompany?.value ?? '');
        }
    }, [getCompaniesList]);

    useEffect(() => {
        if (formik.values.company) {
            updateHookFilters({ filter_filters: { companyId: formik.values.company } });
        }
    }, [formik.values.company]);

    useEffect(() => {
        if (getClientsList().length > 0 && !isSearchedForClient && clientId) {
            setIsSearchedForClient(true);
            let defaultCompany = getClientsList()?.find((g: any) => g.value == clientId);
            formik.setFieldValue('company', defaultCompany?.companyId ?? '');
        }
    }, [getClientsList]);

    // RENDER

    return (
        <div className="@container">
            <form onSubmit={formik.handleSubmit} ref={formRef ? formRef : undefined}>
                {isLocked && (
                    <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                        Este documento está {invoiceSelected?.isCancel ? 'cancelado' : 'pagado'}. Solo puedes modificar el comentario.
                    </div>
                )}
                <div className="mb-10 grid gap-4 divide-y-2 divide-dashed divide-gray-200 @2xl:gap-9 @3xl:gap-11 font-medium">
                    <div className={'md:col-span-12'}>
                        <div className='md:grid md:grid-cols-12 gap-4'>

                            <RequiredInput
                                id='invoiceDate'
                                label="Fecha de la factura"
                                type="date"
                                className='md:col-span-12'
                                formik={formik}
                                error={formik.errors.invoiceDate}
                                disabled={isLocked}
                            />

                            <RequiredInput
                                id='kgQuantity'
                                label="Cantidad (kg)"
                                type="number"
                                className='md:col-span-6'
                                formik={formik}
                                error={formik.errors.kgQuantity}
                                disabled={isLocked}
                            />

                            <RequiredInput
                                id='unitPrice'
                                label="Precio unitario"
                                type="number"
                                className='md:col-span-6'
                                formik={formik}
                                error={formik.errors.unitPrice}
                                disabled={isLocked}
                            />

                            <RequiredInput
                                id='ivaTax'
                                label="IVA (%)"
                                type="number"
                                className='md:col-span-6'
                                formik={formik}
                                error={formik.errors.ivaTax}
                                disabled={isLocked}
                            />

                            <Input
                                id='comments'
                                label="Comentarios"
                                type="text"
                                className='md:col-span-12'
                                value={formik.values.comments}
                                onChange={(e: any) => { formik.setFieldValue('comments', e.target.value) }}
                                error={formik.touched.comments && formik.errors.comments ? (typeof formik.errors.comments === 'string' ? formik.errors.comments : '') : ''}
                            />

                            <CustomSelect
                                required
                                isSearchable
                                id={'clientId'}
                                label="Cliente"
                                containerClassName='md:col-span-12'
                                value={{ value: formik.values.clientId, label: getClientsList()?.find((g: any) => g.value === formik.values.clientId)?.label }}
                                options={getClientsList()}
                                onChange={(e: any) => { formik.setFieldValue('clientId', e?.value) }}
                                error={formik.errors.company}
                                isDisabled={isLocked || clientId !== null}
                            />

                            {userCan('list', 'company') && (
                                <CustomSelect
                                    required
                                    isSearchable
                                    id={'company'}
                                    label="Compañia"
                                    containerClassName='md:col-span-12'
                                    options={getCompaniesList()}
                                    value={
                                        formik.values.company ?
                                        { value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label } :
                                        getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU')
                                    }
                                    onChange={(e: any) => { formik.setFieldValue('company', e?.value) }}
                                    error={formik.errors.company}
                                    isDisabled={isLocked || clientId !== null}
                                />
                            )}

                        </div>
                    </div>
                </div>
            </form>
        </div>
    )
}

export default InvoiceForm;